import React, { useRef, useState, useCallback } from 'react';
import styles from './StepCard.module.css';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

interface StepCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  illustration?: React.ReactNode;
  illustrationPath?: string;
  animationDelay?: number;
}

const StepCard: React.FC<StepCardProps> = ({ 
  icon, 
  title, 
  description, 
  illustration,
  illustrationPath,
  animationDelay = 0
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [rotateX, setRotateX] = useState<number>(0);
  const [rotateY, setRotateY] = useState<number>(0);
  const animationRef = useRef<number | null>(null);
  
  const { elementRef, isVisible } = useScrollAnimation({
    threshold: 0.1,
    triggerOnce: true,
    delay: animationDelay
  });

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;

    // Cancel previous animation frame
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    animationRef.current = requestAnimationFrame(() => {
      const rect = cardRef.current!.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      const deltaX = e.clientX - centerX;
      const deltaY = e.clientY - centerY;
      
      // Smoother rotation with reduced intensity
      const rotateXValue = (deltaY / (rect.height / 1.9)) * -8;
      const rotateYValue = (deltaX / (rect.width / 1.9)) * 8;
      
      setRotateX(rotateXValue);
      setRotateY(rotateYValue);
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    // Cancel any pending animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    
    setRotateX(0);
    setRotateY(0);
  }, []);

  return (
    <div 
      ref={(el) => {
        cardRef.current = el;
        elementRef.current = el;
      }}
      className={`${styles.card} timeline-card`}
      style={{
        transformStyle: 'preserve-3d',
        opacity: isVisible ? 1 : 0,
        transform: isVisible 
          ? `perspective(500px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(0)`
          : `perspective(500px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(30px)`,
        transition: 'opacity 0.3s ease-out, transform 0.3s ease-out',
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div className={styles.content}>
        {/* <div className={styles.icon}>
          {icon}
        </div> */}
        <div className={styles.text}>
          <h3 className={styles.title}>
            {title}
          </h3>
          <p className={`${styles.subtitle} text-balance`}>
            {description}
          </p>
        </div>
      </div>
      {/* Illustration or placeholder */}
      <div className={styles.illustration}>
        {illustration || (illustrationPath ? (
          <img 
            src={illustrationPath} 
            alt="Step illustration" 
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'contain',
              filter: 'grayscale(100%)',
              opacity: 0.3
            }}
          />
        ) : (
          <svg width="180" height="120" viewBox="0 0 180 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="180" height="120" rx="12" fill="#E5F3FF"/>
            <rect x="30" y="30" width="120" height="60" rx="8" fill="#B8D4F0"/>
            <circle cx="60" cy="50" r="8" fill="#0B63FF"/>
            <rect x="80" y="45" width="60" height="4" rx="2" fill="#0B63FF"/>
            <rect x="80" y="55" width="40" height="3" rx="1.5" fill="#6B6B6B"/>
          </svg>
        ))}
      </div>
    </div>
  );
};

export default StepCard;
